/**
 * Server-side in-memory cache for resolver API data.
 *
 * Lives at the Node.js module level (outside Angular's DI), so it persists
 * across SSR requests. This prevents re-fetching from the backend API on every
 * SSR render — even when the HTML cache expires and triggers a re-render, the
 * resolver data is served from this cache.
 *
 * Uses a stale-while-revalidate pattern: stale data is returned immediately
 * while a background refresh is triggered, so SSR renders are never blocked
 * by slow backend API calls after the initial warm-up.
 */

interface CacheEntry<T> {
  data: T;
  expiry: number;
}

const cache = new Map<string, CacheEntry<unknown>>();
const pendingRefreshes = new Set<string>();

const DEFAULT_TTL = 300_000; // 5 minutes

export interface ServerCacheResult<T> {
  data: T | null;
  /** true when the returned data is past its TTL (still usable, but should be refreshed) */
  stale: boolean;
}

/**
 * Get cached data with stale-while-revalidate semantics.
 * Returns `{ data, stale }` — callers should trigger a background refresh when `stale` is true.
 */
export function getServerCache<T>(key: string): ServerCacheResult<T> {
  const entry = cache.get(key);
  if (!entry) return { data: null, stale: false };

  if (Date.now() > entry.expiry) {
    return { data: entry.data as T, stale: true };
  }

  return { data: entry.data as T, stale: false };
}

export function setServerCache<T>(key: string, data: T, ttl = DEFAULT_TTL): void {
  cache.set(key, { data, expiry: Date.now() + ttl });
  pendingRefreshes.delete(key);
}

/** Mark a key as currently being refreshed (prevents duplicate concurrent refreshes) */
export function markRefreshing(key: string): boolean {
  if (pendingRefreshes.has(key)) return false;
  pendingRefreshes.add(key);
  return true;
}
